import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useGroupStore } from '@/stores/groupStore';
import { useAuthStore } from '@/stores/authStore';

export function InvitePage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { inviteInfo, isLoading, error, resolveInvite, acceptInvite } = useGroupStore();
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  useEffect(() => {
    if (code) {
      resolveInvite(code);
    }
  }, [code]);

  const handleAccept = async () => {
    if (!code) return;
    setIsAccepting(true);
    setAcceptError(null);
    try {
      await acceptInvite(code);
      if (inviteInfo?.groupId) {
        navigate(`/groups/${inviteInfo.groupId}`);
      } else {
        navigate('/groups');
      }
    } catch (err) {
      setAcceptError(err instanceof Error ? err.message : 'Failed to accept invite');
    } finally {
      setIsAccepting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !inviteInfo) {
    return (
      <div className="text-center py-12 px-4">
        <div className="max-w-sm mx-auto">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
            <XIcon className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-card-foreground mb-2">Invalid Invite</h2>
          <p className="text-muted-foreground text-sm">
            {error || 'This invite link is invalid or has expired.'}
          </p>
          <Link
            to="/groups"
            className="mt-4 inline-block text-sm text-primary hover:underline"
          >
            Browse Groups
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center py-12 px-4">
      <div className="max-w-sm w-full">
        <div className="bg-card border border-border rounded-xl p-6 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
            <GroupIcon className="w-8 h-8 text-primary" />
          </div>

          <h2 className="text-xl font-bold text-card-foreground">
            {inviteInfo.groupName}
          </h2>

          {inviteInfo.groupDescription && (
            <p className="text-sm text-muted-foreground mt-2">
              {inviteInfo.groupDescription}
            </p>
          )}

          <div className="flex items-center justify-center gap-2 mt-3 text-sm text-muted-foreground">
            <UsersIcon className="w-4 h-4" />
            <span>{inviteInfo.memberCount} members</span>
          </div>

          {inviteInfo.inviterName && (
            <p className="text-sm text-muted-foreground mt-2">
              Invited by <span className="font-medium">{inviteInfo.inviterName}</span>
            </p>
          )}

          {acceptError && acceptError.toLowerCase().includes('already') ? (
            // Already a member - show friendly UI
            <div className="mt-4">
              <div className="p-3 bg-primary/10 text-primary rounded-lg text-sm mb-4 flex items-center justify-center gap-2">
                <CheckIcon className="w-4 h-4" />
                <span>You're already a member!</span>
              </div>
              <Link
                to={`/groups/${inviteInfo.groupId}`}
                className="block w-full py-3 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors text-center"
              >
                Go to Group
              </Link>
            </div>
          ) : acceptError ? (
            <div className="mt-4 p-3 bg-red-500/10 text-red-500 rounded-lg text-sm">
              {acceptError}
            </div>
          ) : null}

          {isAuthenticated && !acceptError?.toLowerCase().includes('already') ? (
            <button
              onClick={handleAccept}
              disabled={isAccepting || !inviteInfo.isValid}
              className="mt-6 w-full py-3 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {isAccepting ? 'Joining...' : inviteInfo.isValid ? 'Accept Invite' : 'Invite Expired'}
            </button>
          ) : !isAuthenticated ? (
            <div className="mt-6">
              <p className="text-sm text-muted-foreground mb-3">
                Sign in to accept this invite
              </p>
              <Link
                to="/login"
                className="inline-block w-full py-3 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors text-center"
              >
                Sign In
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function GroupIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
